import styled from 'styled-components'
import { Icon, Container } from '@mch-group/uikit-components'
import { px2rem, mq } from '@utils'

import { LinkProps } from 'next/link'
import WithOrWithoutLink from './withOrWithoutLink'
import styles from '../styles/index.module.scss'
import useOVRNavigator from './hooks/useOVRNavigator'
import { FC } from 'react'
import { useLabelsFromAPI } from '@dmi-mch/hooks'
import { getSiteLanguage } from '@utils'

/**
 * @returns {JSX.Element}
 * @author: Josep Vidal
 * @constructor
 */
const OVRNavigator: FC<IOVRNavigator> = ({
  fluid = false,
  isLoadingNextRooms = false,
  leftSideContentVisible = true,
  leftSideIcon,
  nextJSLinkInLeftSideArrow,
  nextJSLinkInRightSideLeftArrow,
  nextJSLinkInRightSideRightArrow,
  onLeftSideArrowClick = null,
  onRightSideLeftArrowClick = null,
  onRightSideRightArrowClick = null,
  rightSideCenterContent,
  rightSideContentVisible = true,
  rightSideDisabled = false,
  rightSideLeftText,
  navigationKey,
  a11yData = {
    previousPage: '',
    nextPage: ''
  }
}) => {
  const {
    backLinkStored,
    leftArrowRef,
    rightArrowRef,
    rightSideCenterContentVisible,
    rightSideLeftArrowVisible,
    rightSideRightArrowVisible
  } = useOVRNavigator({
    nextJSLinkInRightSideLeftArrow,
    nextJSLinkInRightSideRightArrow,
    onRightSideLeftArrowClick,
    onRightSideRightArrowClick,
    rightSideContentVisible,
    rightSideDisabled,
    navigationKey
  })
  const { labels } = useLabelsFromAPI(['AltTags'], getSiteLanguage())

  return (
    <nav aria-label='ovr-navigator' className={styles.StyledOVRNavigator}>
      <Container className={`${styles.ovrnavigatorContainer} px-5 px-lg-8 px-md-7 w-100`} fluid={fluid}>
        {/* LEFT ARROW (BACK TO OVR, BACK TO SEARCH, BACK TO COLLECTION, BACK TO GALLERY) */}
        <LeftSideArrow onClick={onLeftSideArrowClick}>
          {leftSideContentVisible ? (
            <WithOrWithoutLink
              backLinkStored={backLinkStored}
              nextJSLinkProps={nextJSLinkInLeftSideArrow}
              title={labels?.AltTags?.previousPage || 'Go to previous page'}
            >
              {leftSideIcon === 'close' ? (
                <Icon
                  name='close'
                  //@ts-ignore
                  svgTitle={labels?.AltTags?.closePage || 'Close page'} className='icon-close' />
              ) : (
                <Icon
                  name='chevron-left'
                  className='icon-left'
                  svgTitle={labels?.AltTags?.previousPage || 'Go to previous page'}
                />
              )}
            </WithOrWithoutLink>
          ) : null}
        </LeftSideArrow>
        {/* PREVIOUS / NEXT ARROWS */}
        {rightSideContentVisible ? (
          <RightSideContent $rightSideDisabled={rightSideDisabled}>
            {rightSideLeftText && (
              <RightSideLeftText className='header-uppercase-2 mt-2'>{rightSideLeftText}</RightSideLeftText>
            )}
            {rightSideLeftArrowVisible &&
              (isLoadingNextRooms ? (
                <RightSideLeftArrow>
                  <WithOrWithoutLink
                    title={a11yData?.previousPage || labels?.AltTags?.previousPage || 'Go to previous page'}
                  >
                    <Icon
                      name='chevron-left'
                      className='icon-left'
                      svgTitle={a11yData?.previousPage || labels?.AltTags?.previousPage || 'Go to previous page'}
                    />
                  </WithOrWithoutLink>
                </RightSideLeftArrow>
              ) : (
                <RightSideLeftArrow ref={leftArrowRef} onClick={onRightSideLeftArrowClick || null}>
                  <WithOrWithoutLink
                    nextJSLinkProps={nextJSLinkInRightSideLeftArrow}
                    title={a11yData?.previousPage || labels?.AltTags?.previousPage || 'Go to previous page'}
                  >
                    <Icon
                      name='chevron-left'
                      className='icon-left'
                      svgTitle={a11yData?.previousPage || labels?.AltTags?.previousPage || 'Go to previous page'}
                    />
                  </WithOrWithoutLink>
                </RightSideLeftArrow>
              ))}
            {rightSideCenterContentVisible && <RightSideCenterText>{rightSideCenterContent}</RightSideCenterText>}
            {rightSideRightArrowVisible &&
              (isLoadingNextRooms ? (
                <RightSideRightArrow>
                  <WithOrWithoutLink title={a11yData?.nextPage || labels?.AltTags?.nextPage || 'Go to next page'}>
                    <Icon
                      name='chevron-right'
                      className='icon-right'
                      svgTitle={a11yData?.nextPage || labels?.AltTags?.nextPage || 'Go to next page'}
                    />
                  </WithOrWithoutLink>
                </RightSideRightArrow>
              ) : (
                <RightSideRightArrow ref={rightArrowRef} onClick={onRightSideRightArrowClick || null}>
                  <WithOrWithoutLink
                    nextJSLinkProps={nextJSLinkInRightSideRightArrow}
                    title={a11yData?.nextPage || labels?.AltTags?.nextPage || 'Go to next page'}
                  >
                    <Icon
                      name='chevron-right'
                      className='icon-right'
                      svgTitle={a11yData?.nextPage || labels?.AltTags?.nextPage || 'Go to next page'}
                    />
                  </WithOrWithoutLink>
                </RightSideRightArrow>
              ))}
          </RightSideContent>
        ) : null}
      </Container>
    </nav>
  )
}

const DefaultForArrows = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;

  a {
    position: relative;

    &.icon-left {
      left: -${px2rem(5)};
    }

    &.icon-right {
      right: -${px2rem(5)};
    }
  }

  & > * {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const LeftSideArrow = styled(DefaultForArrows)<any>`
  height: 100%;

  a {
    left: -${px2rem(5)};
  }

  & > * {
    padding: 0 ${px2rem(17)} 0 0;
  }
`

const RightSideContent = styled.div<any>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: ${({ $rightSideDisabled }) => ($rightSideDisabled ? 0.6 : 1)};
  transition: opacity 0.35s ease-in-out;
  height: 100%;
`

const RightSideLeftText = styled.h1`
  color: var(--bs-mch-gray-400) !important;
  ${mq.lessThan('largestMobile')`
    display : none;
  `}
`

const RightSideLeftArrow = styled(DefaultForArrows)<any>`
  height: 100%;

  a {
    position: relative;
    right: -${px2rem(5)};

    &:not([href]) {
      opacity: 50%;
      pointer-events: none !important;
    }
  }

  & > * {
    padding: 0 ${px2rem(22)};
  }
`

const RightSideCenterText = styled.div`
  padding: 0 8px;
  height: 100%;
  display: flex;
  align-content: center;
  align-items: center;
`

const RightSideRightArrow = styled(DefaultForArrows)<any>`
  height: 100%;

  a {
    position: relative;
    right: -${px2rem(5)};
  }

  & > * {
    padding: 0 0 0 ${px2rem(12)};
  }
`

interface IOVRNavigator {
  fluid?: boolean
  isLoadingNextRooms: boolean
  leftSideContentVisible?: any
  leftSideIcon?: 'close' | 'icon'
  nextJSLinkInLeftSideArrow?: LinkProps
  nextJSLinkInRightSideLeftArrow?: LinkProps
  nextJSLinkInRightSideRightArrow?: LinkProps
  onLeftSideArrowClick?: null | (() => void)
  onRightSideLeftArrowClick?: null | (() => void)
  onRightSideRightArrowClick?: null | (() => void)
  rightSideCenterContent?: React.ReactNode
  rightSideContentVisible?: boolean
  rightSideDisabled?: boolean
  rightSideLeftArrowVisible?: boolean
  rightSideLeftText?: string
  rightSideText?: string
  navigationKey?: string
  a11yData?: {
    previousPage?: string
    nextPage?: string
  }
}

export default OVRNavigator
